<template>
  <div>
    <div v-if="!showMessageUploadView" class="columns">
      <b-field
        :disabled="!enableMessaging"
        :style="'color:' + unresolvedComments.color"
        :label="'Unresolved comments (UC): ' + unresolvedComments.value"
        class="column comments_status-title"
      >
        <b-button
          :disabled="!enableMessaging"
          v-if="unresolvedComments.value !== nextStatus"
          @click="updateUC(getAdvisorNextStatusOptions)"
          :style="
            'color: white; background-color:' + getUnresolvedCommentsButtonColor
          "
          >SET STATUS TO {{ nextStatus }}</b-button
        >
        <b-field v-else>
          <b-tag icon="information-outline"
            >Currently awaiting Admin approval</b-tag
          >
        </b-field>
      </b-field>
    </div>
    <div v-if="!showMessageUploadView" class="messages" ref="allMessages">
      <div
        class="student_message"
        v-for="(message, index) in mergeMessagesAndDocuments"
        :key="index"
        :class="getBubbleDirection(message)"
      >
        <bubble :message="message"></bubble>
      </div>
    </div>

    <div v-if="!showMessageUploadView" class="new-message">
      <div class="new-message__actions">
        <b-button
          :disabled="!enableMessaging"
          @click="toggleShowInvite()"
          size="is-med"
          icon-left="account-multiple-plus-outline"
          type="is-info"
          class="mr-5"
          title="Invite users to this chat"
        ></b-button>

        <div
          class="column d-flex invite__holder"
          v-if="showInvite"
          v-click-outside="toggleShowInvite"
        >
          <div
            v-if="enableMessaging"
            class="column message-input"
            v-for="invites in filteredApplicationInvites"
          >
            <span
              ><b>{{ getUsername(invites.moderatorId) }}</b> invited
              <b>{{ getUsername(invites.inviteModeratorId) }}</b> on
              <b>{{ convertedMessageTime(invites.createdAt) }}</b></span
            >
            <b-button
              @click="
                removeAppInvite(
                  invites.applicationId,
                  invites.moderatorId,
                  invites.inviteModeratorId,
                  true
                )
              "
              type="is-danger"
              class="ml-2"
              >Remove invite</b-button
            >
          </div>

          <multiselect
            v-model="inviteModerators"
            :options="mappedUsernames"
            track-by="value"
            label="value"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            placeholder="Invite users to this conversation"
            :preselect-first="false"
          >
          </multiselect>
          <b-button
            type="is-success"
            class="invite__users-message"
            icon-left="send-circle"
            @click.prevent="inviteUsersToConversation()"
          />
        </div>

        <b-tooltip
          type="is-light"
          :triggers="['hover', 'click']"
          multilined
          auto-close
          position="is-top"
        >
          <template v-slot:content>
            <ul>
              <li
                v-for="(document, index) in getRequiredDocs"
                v-bind:key="index"
                :class="document.uploaded ? 'uploaded req-doc' : 'req-doc'"
              >
                {{
                  document.name.length
                    ? document.name
                    : 'No Document type selected'
                }}
              </li>
            </ul>
          </template>
          <b-button label="Documents" type="is-light" />
        </b-tooltip>

        <b-button
          @click="toggleShowMessageUploadView()"
          type="status-success"
          class="ml-5 text-light"
          >Upload documents ({{ getStudentDocsFromApplication.length }} docs
          uploaded)</b-button
        >
      </div>

      <b-field
        class="column message-input no-label"
        :disabled="!enableMessaging"
      >
        <b-input
          placeholder="Write a message"
          :disabled="!enableMessaging"
          v-model="application_message"
          type="textarea"
          name="application_message"
          @keyup.native.enter="addCustomMessage()"
        ></b-input>
      </b-field>
      <b-button
        size="is-med"
        class="message-send"
        rounded
        @click="addCustomMessage()"
        icon-left="send-circle-outline"
        :disabled="!enableMessaging"
      >
        Send
      </b-button>
    </div>

    <div
      v-if="showMessageUploadView"
      class="position-relative"
      :style="{
        'min-height': `${allMessagesHeight}px`,
      }"
    >
      <div
        class="docs__view-mode"
        v-if="
          getStudentDocsFromApplication && getStudentDocsFromApplication.length
        "
      >
        <h3 class="edit-title">Uploaded documents</h3>
        <div class="divider"></div>
        <div
          class="doc-title-holder doc-title-holder__view-mode"
          v-for="(file, index) in uploadedStudentDocuments"
          :key="index"
        >
          <app-doc-icon :document="file.name"></app-doc-icon>
          <b-button
            type="is-link"
            class="message-documents__download"
            @click="downloadItem(file)"
            icon-left="download"
          ></b-button>
          <p class="doc-title" :title="file.name">
            {{ file.name }}
            <br />
            <b v-if="file.docType && file.docType.length">
              {{ file.docType }}
            </b>
            <br v-if="file.updatedAt" />
            <span v-if="file.updatedAt" class="small">
              Last Modified: {{ file.updatedTime }}</span
            >
            <br v-if="file.createdAt" />
            <span v-if="file.createdAt" class="small">
              First uploaded: {{ file.createdTime }}</span
            >
            <br v-if="file.uploader_name" />
            <span v-if="file.uploader_name" class="small">
              Uploaded by: {{ file.uploader_name }}</span
            >
          </p>
        </div>
      </div>
      <div>
        <b-button
          v-if="newDocs.length"
          type="is-primary"
          class="mr-5"
          @click="submitDocs()"
          >Upload new docs
        </b-button>

        <b-button
          @click="toggleShowMessageUploadView()"
          type="status-success"
          class="text-light"
          >Go back to chat</b-button
        >

        <b-field
          class="file is-primary my-5"
          :class="{ 'has-name': !!newDocs }"
        >
          <b-upload
            :native="true"
            multiple
            drag-drop
            v-model="newDocs"
            :disabled="!enableMessaging"
          >
            <span class="file-cta my-3">
              <b-icon class="file-icon" icon="upload"></b-icon>
              <span class="file-label">Click to upload</span>
            </span>
            <span class="file-name chat-files" v-if="newDocs">
              <div v-for="(file, index) in newDocs" class="chat-file">
                <div class="column is-full">
                  {{ file.name }}
                </div>
                <b-field
                  label="Document type"
                  class="column is-full"
                  :type="{ 'is-danger': docTypeErrors.includes(index) }"
                  :message="
                    docTypeErrors.includes(index) ? 'Value cannot be empty' : ''
                  "
                >
                  <b-autocomplete
                    name="document_type"
                    placeholder="Document type"
                    open-on-focus
                    v-model="file.docType"
                    :data="filteredDocType(index)"
                    icon="magnify"
                    clearable
                  >
                    <template #empty>No results found</template>
                  </b-autocomplete>
                </b-field>
              </div>
            </span>
          </b-upload>
        </b-field>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import StudentService from '../../services/student.service';
import Bubble from '../helpers/Bubble.vue';
import AppDocIcon from '../helpers/DocIcon.vue';

import UtilService from '../../services/util';
import downloadBlob from '../../services/file-download';

import { docType, studentMessageStatusMap } from '../../models/static';
import moment from 'moment';
export default {
  name: 'StudentMessage',
  components: {
    Bubble,
    Multiselect,
    AppDocIcon,
  },
  data() {
    return {
      inviteModerators: [],
      application_message: '',
      originalMessages: [],
      showInvite: false,
      showMessageUploadView: false,
      allMessagesHeight: 600,
      newDocs: [],
      docTypeErrors: [],
    };
  },
  async mounted() {
    if (UtilService.getMessagesForConversation().length <= 10) {
      await StudentService.getStudentMessages(
        this.getActiveConversation.id
      ).then((response) => {
        this.$store.dispatch('application/updateStudentMessages', {
          studentId: this.getActiveConversation.studentId,
          messages: response.data.application_messages,
        });
      });

      this.$store.dispatch('message/setInitialMessages', {
        messages: this.$store.state.application.applications,
        userId: this.currentUser.id,
      });

      // updateStudentMessages
    }

    if (this.enableMessaging) {
      this.markMessagesAsSeen();
    }
  },
  watch: {},
  computed: {
    allDocTypes() {
      return [
        ...new Set([
          ...this.getRequiredDocs.map((doc) => doc.name),
          ...docType,
        ]),
      ];
    },
    mappedUsernames() {
      return UtilService.multiSelectMappedUsernames();
    },
    mappedCourseModerators() {
      return this.$store.state.application.courseMapModerator
        .filter(
          (course) => course.title === this.getActiveConversation.courseTitle
        )
        .map((course) => course.moderatorId)
        .flat();
    },
    getRequiredDocs() {
      return [
        ...new Set([
          ...this.getActiveConversation.courseDocuments.map((doc) => {
            return {
              name: doc,
              uploaded: this.getStudentDocsFromApplication
                .map((doc) => doc.docType.toLowerCase())
                .includes(doc.toLowerCase()),
            };
          }),

          ...this.getStudentDocsFromApplication.map((doc) => {
            return this.getActiveConversation.courseDocuments.includes(
              doc.docType
            )
              ? null
              : {
                  name: doc.docType,
                  uploaded: 'extra',
                };
          }),
        ]),
      ].filter((x) => x);
    },
    enableMessaging() {
      return (
        this.$store.state.application.courseMapModerator.filter(
          (course) =>
            course.title === this.getActiveConversation.courseTitle &&
            course.moderatorId.includes(this.currentUser.id)
        ).length ||
        this.currentUser.id ===
          UtilService.getActiveConversation().moderatorId ||
        this.$store.state.message.invites.filter(
          (message) =>
            message.applicationId === this.getActiveConversation.id &&
            message.inviteModeratorId === this.currentUser.id
        ).length ||
        !UtilService.getActiveConversation().isApproved ||
        UtilService.isAdmin()
      );
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    isManagement() {
      return UtilService.isManagement();
    },
    uploadedStudentDocuments() {
      return UtilService.getStudentDocsFromApplication().map((doc) => {
        return {
          name: doc.originalName,
          path: doc.path,
          docType: doc.docType,
          updatedAt: doc.updatedAt,
          createdAt: doc.createdAt,
          uploader_name: doc.uploader_name,
          updatedTime: this.formatDate(doc.updatedAt),
          createdTime: this.formatDate(doc.createdAt),
        };
      });
    },
    filteredApplicationInvites() {
      return this.$store.state.message.invites.filter(
        (invite) => invite.applicationId === this.getActiveConversation.id
      );
    },
    getActiveConversation() {
      return UtilService.getActiveConversation();
    },
    mergeMessagesAndDocuments() {
      return [
        ...UtilService.getMessagesForConversation(),
        ...this.uploadedStudentDocuments,
      ].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    },
    getMessagesForConversation() {
      return UtilService.getMessagesForConversation();
    },
    getStudentDocsFromApplication() {
      return UtilService.getStudentDocsFromApplication();
    },
    getCurrentAppUnresolvedComments() {
      return UtilService.getUnresolvedCommentsFromApplication();
    },
    nextStatus() {
      return studentMessageStatusMap.filter(
        (status) => status.id === this.getAdvisorNextStatusOptions
      )[0].value;
    },
    getAdvisorNextStatusOptions() {
      return this.getActiveConversation.moderatorId !== this.currentUser.id
        ? this.unresolvedComments.manager.nextAction
        : this.unresolvedComments.advisor.nextAction;
    },
    getUnresolvedCommentsButtonColor() {
      return this.getActiveConversation.moderatorId !== this.currentUser.id
        ? this.unresolvedComments.manager.color
        : this.unresolvedComments.advisor.color;
    },
    unresolvedComments: {
      get() {
        const foundStatusMessage = studentMessageStatusMap.filter(
          (status) => status.id === this.getCurrentAppUnresolvedComments
        );
        return foundStatusMessage && foundStatusMessage.length
          ? foundStatusMessage[0]
          : studentMessageStatusMap[0];
      },
      set(value) {
        let editValue =
          this.getActiveConversation.moderatorId !== this.currentUser.id &&
          value === false
            ? null
            : value;
        this.updateUC(editValue);
      },
    },
    unresolvedCommentsType() {
      switch (this.getCurrentAppUnresolvedComments) {
        case true:
          return 'is-danger';

        case false:
          return 'is-warning';

        default:
          return 'is-dark';
      }
    },
  },
  methods: {
    selectedFileType(value, index) {
      this.newDocs[index].docType = value;
    },
    getUsername(userId) {
      const foundUser = this.mappedUsernames.filter(
        (user) => user.id === userId
      );
      return foundUser && foundUser.length
        ? foundUser[0].value
        : 'User not found';
    },
    convertedMessageTime(message) {
      return moment(message.createdAt).calendar();
    },
    submitDocs() {
      this.docTypeErrors = [];
      this.newDocs.map((doc, index) => {
        return doc.docType === undefined || !doc.docType.length
          ? this.docTypeErrors.push(index)
          : null;
      });
      if (this.docTypeErrors.length) {
        return;
      }
      let formData = new FormData();
      formData.append('fileChanges', true);
      formData.append('isAdminApproved', true);

      formData.append('onlyDocs', true);
      formData.append('moderatorName', this.currentUser.username);
      formData.append('moderatorId', this.currentUser.id);

      for (const [index, file] of this.newDocs.entries()) {
        formData.append('files', file);
        formData.append('fileTypes', this.newDocs[index].docType);
      }

      formData.append('id', this.getActiveConversation.studentId);
      formData.append('docModeratorName', this.currentUser.username);

      // return
      StudentService.create(formData).then(() => {
        // TO-DO Also update the docs using the websocket
        this.$buefy.toast.open({
          duration: 3000,
          message: 'Added docs succesfully',
          position: 'is-bottom',
          type: 'is-success',
        });

        this.$eventHub.$emit('socket__event', {
          messageType: 'socket__new-student',
          messageValue: {
            message: `${this.currentUser.username} added new documents for ${this.getActiveConversation.studentName}`,
            team: UtilService.getUserTeam(UtilService.currentUserTeam()),
          },
        });

        this.newDocs = [];
      });
    },
    filteredDocType(index) {
      if (
        this.newDocs[index].docType === undefined ||
        !this.newDocs[index].docType
      ) {
        this.newDocs[index].docType = '';
      }
      return this.allDocTypes;
    },

    async downloadItem(message) {
      const url = message.path.replace('dist', '');
      const download = message.name;
      downloadBlob(url, download);
    },

    toggleShowInvite() {
      setTimeout(() => {
        this.showInvite = !this.showInvite;
      }, 1);
    },

    formatDate(dateString) {
      if (dateString && new Date(dateString).toString() !== 'Invalid Date') {
        return moment(dateString).format('DD-MM-YYYY, h:mm:ss a');
      }

      return '';
    },

    toggleShowMessageUploadView() {
      this.allMessagesHeight = 600;
      if (this.$refs.allMessages) {
        this.allMessagesHeight =
          this.$refs.allMessages.clientHeight + 100 < 600
            ? 600
            : this.$refs.allMessages.clientHeight + 100;
      }

      setTimeout(() => {
        this.showMessageUploadView = !this.showMessageUploadView;
      }, 1);
    },

    addCustomMessage() {
      if (!this.application_message.length || !this.enableMessaging) {
        return;
      }

      const message = {
        message: this.application_message,
        status: 'Required',
        moderatorId: this.currentUser.id,
        advisorName: this.currentUser.username,
        moderatorName: this.currentUser.username,
        isUnread: true,
        applicationId: this.getActiveConversation.id,
        category: 'Today',
      };

      this.application_message = '';
      this.$eventHub.$emit('socket__event', {
        messageType: 'socket__new-message',
        messageValue: {
          ...message,
          ...this.getActiveConversation,
        },
      });
      StudentService.insertMessage(message);
    },

    updateUC(editValue) {
      this.$eventHub.$emit('socket__event', {
        messageType: 'socket__edit-application',
        messageValue: {
          studentValue: this.getActiveConversation.id,
          value: editValue,
          path: 'unresolvedComments',
          setData: true,
        },
      });
      StudentService.updateStatus({
        id: this.getActiveConversation.id,
        unresolvedComments: editValue,
      }).then(() => {
        this.$buefy.toast.open({
          duration: 3000,
          message: `Unresolved comments updated succesfully`,
          position: 'is-bottom',
          type: 'is-success',
        });
      });
    },

    getBubbleDirection(message) {
      if (message && message.hasOwnProperty('moderatorId')) {
        return message.moderatorId === this.currentUser.id ? 'right' : 'left';
      }

      return 'left';
    },

    inviteUsersToConversation() {
      this.removeAppInvite(
        UtilService.getActiveConversation().id,
        this.currentUser.id,
        this.inviteModerators.map((user) => user.id),
        false
      );
    },

    removeAppInvite(
      applicationId,
      moderatorId,
      inviteModeratorId,
      shouldDelete
    ) {
      const data = {
        applicationId,
        moderatorId,
        inviteModeratorId,
        shouldDelete,
      };

      let newInvites = [];

      if (shouldDelete) {
        newInvites = this.$store.state.message.invites.filter(
          (message) =>
            message.applicationId !== applicationId ||
            message.moderatorId !== moderatorId ||
            message.inviteModeratorId !== inviteModeratorId
        );
      } else {
        newInvites = [
          ...data.inviteModeratorId.map((invModeratorId) => {
            return {
              inviteModeratorId: invModeratorId,
              moderatorId: data.moderatorId,
              applicationId: data.applicationId,
              updatedAt: new Date(),
              createdAt: new Date(),
            };
          }),
          ...this.$store.state.message.invites,
        ];
      }

      this.$store.dispatch('message/setInvites', newInvites);
      StudentService.messageInvite(data);
    },

    markMessagesAsSeen() {
      const currentUnreadMessages = this.getMessagesForConversation
        .map((message) => {
          return {
            id: message.id,
            moderatorId: message.moderatorId,
            isUnread: message.isUnread,
          };
        })
        .filter((message) => message.isUnread);

      const unseenMessages = currentUnreadMessages.filter(
        (message) =>
          message.moderatorId !== this.currentUser.id &&
          (this.currentUser.id ===
            UtilService.getActiveConversation().moderatorId ||
            this.mappedCourseModerators.includes(this.currentUser.id))
      );
      // IN case the message is sent by someone else beside the moderator of the student
      // it should not be marked as seen unless the person that reads the message is the one that submitted the student

      const unseenMessageIds = unseenMessages.map((message) => message.id);
      if (!unseenMessageIds.length) {
        return;
      }

      StudentService.updateSeenMessages({
        id: unseenMessageIds,
        moderatorId: this.currentUser.id,
      }).then(() => {
        this.$eventHub.$emit('socket__event', {
          messageType: 'socket__edit-message',
          messageValue: {
            ids: unseenMessageIds,
            isUnread: false,
          },
        });
      });
    },
  },
};
</script>

<style>
.messages {
  max-height: calc(60vh - 18px);
  overflow-y: scroll;
  overflow-x: hidden;
  display: flex;
  flex-direction: column-reverse;
}

.messages .student_message {
  display: flex;
  align-items: center;
  margin: 12px;
  position: relative;
  background: #fff;
}

.messages .student_message.right {
  justify-content: flex-end;
}

.file-name.chat-files {
  display: flex;
  flex-direction: row;
  overflow: visible;
  height: auto;
  align-items: flex-start;
  max-width: none;
  flex-wrap: wrap;
  border: none;
}

.file-name.chat-files .chat-file {
  display: flex;
  flex-direction: column;
}

.file-name.chat-files .chat-file > .field {
  margin: 12px 0;
}

.grey-message {
  background: #ffe0e0;
}

.blue-message {
  background: #5d9cec;
}

.blue-message .global-message-author {
  color: #ffe0e0;
}

.comments_status-title > label {
  color: inherit;
}

.new-message__actions {
  flex-basis: 100%;
  margin-left: 6px;
  padding-top: 20px;
}

.new-message__actions .invite__holder {
  position: absolute;
  left: 20px;
  background: white;
  bottom: 100%;
  padding-bottom: 20px;
  min-width: 500px;
  flex-wrap: wrap;
}

.new-message__actions .invite__holder .multiselect {
  flex-basis: calc(100% - 50px);
}

.new-message__actions .invite__holder > button {
  flex-basis: 40px;
}

.new-message__actions .message-input {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  flex-basis: 100%;
  align-items: center;
}

.invite__users-message {
  flex: 10%;
  margin-left: 8px;
  margin-top: 3px;
}

button.button.message-documents__download {
  position: absolute;
  bottom: -10px;
}
</style>
